import { useState } from "react";
import { signInRoute } from "../router";
import { api, ApiError } from "../lib/api";

type RoleOption = { roleNo: string; name: string; isDefault: boolean };

/**
 * Sign-in with the user's Sertica login (SPEC §12). A user with more than one role that may use
 * the dashboard then chooses the role to sign in with; Sertica's default role is preselected.
 */
export function SignInPage() {
  const { redirect } = signInRoute.useSearch();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<RoleOption[] | null>(null);
  const [roleNo, setRoleNo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function enter() {
    // Back to the same URL, filters included. Only same-site paths are followed.
    const to = redirect && redirect.startsWith("/") && !redirect.startsWith("//") ? redirect : "/";
    window.location.assign(to);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ ok: true; chooseRole?: RoleOption[] }>("/auth/sign-in", { method: "POST", body: JSON.stringify({ login, password }) });
      if (!r.chooseRole) return enter();
      setRoles(r.chooseRole);
      setRoleNo((r.chooseRole.find((x) => x.isDefault) ?? r.chooseRole[0]!).roleNo);
      setPassword("");
      setBusy(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign-in failed.");
      setBusy(false);
    }
  }

  async function chooseRole(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/auth/role", { method: "POST", body: JSON.stringify({ roleNo }) });
      enter();
    } catch (err) {
      // The choice expired or was refused: start over from the password.
      setRoles(null);
      setError(err instanceof ApiError && err.status === 400 ? err.message :"Sign-in timed out. Sign in again.");
      setBusy(false);
    }
  }

  const failure = error && (
    <p role="alert" className="text-[13px] text-[var(--stage-rejected)]">
      {error}
    </p>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-navy text-white px-4 py-3 font-bold">PO Status Dashboard</header>
      <main className="flex-1 flex items-start justify-center px-4 pt-16">
        {roles ? (
          <form onSubmit={chooseRole} className="card w-full max-w-sm p-6 space-y-4" aria-labelledby="role-title">
            <div>
              <h1 id="role-title" className="text-[20px] font-bold">Choose a role</h1>
              <p className="text-muted text-[13px] mt-1">Your Sertica account has more than one role. The role decides what you can see.</p>
            </div>
            <fieldset className="space-y-1">
              <legend className="label">Role</legend>
              {roles.map((r) => (
                <label key={r.roleNo} className={`flex items-center gap-2 rounded-input border px-3 py-2 cursor-pointer ${roleNo === r.roleNo ? "border-brand-2" : "border-line"}`}>
                  <input type="radio" name="role" value={r.roleNo} checked={roleNo === r.roleNo} onChange={() => setRoleNo(r.roleNo)} />
                  <span className="flex-1 text-[14px]">{r.name}</span>
                  {r.isDefault && <span className="text-[12px] text-muted">Default</span>}
                </label>
              ))}
            </fieldset>
            {failure}
            <button disabled={busy || !roleNo} className="w-full rounded-input bg-brand-2 text-white font-semibold py-2 disabled:opacity-60">
              {busy ? "Signing in…" : "Continue"}
            </button>
            <button type="button" className="w-full text-[13px] text-muted" onClick={() => setRoles(null)}>
              Use another account
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="card w-full max-w-sm p-6 space-y-4" aria-labelledby="signin-title">
            <div>
              <h1 id="signin-title" className="text-[20px] font-bold">Sign in</h1>
              <p className="text-muted text-[13px] mt-1">Sign in with your Sertica account</p>
            </div>
            <label className="block">
              <span className="label">Login</span>
              <input className="input w-full mt-1" autoComplete="username" value={login} onChange={(e) => setLogin(e.target.value)} required autoFocus />
            </label>
            <label className="block">
              <span className="label">Password</span>
              <input className="input w-full mt-1" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </label>
            {failure}
            <button disabled={busy} className="w-full rounded-input bg-brand-2 text-white font-semibold py-2 disabled:opacity-60">
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
